import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { feedApi } from '../api';
import type { CreateCommentData } from '../types';

const KEYS = {
  all: ['comments'] as const,
  list: (postId: string) => [...KEYS.all, 'list', postId] as const,
  replies: (postId: string, commentId: string) =>
    [...KEYS.all, 'replies', postId, commentId] as const,
};

export function useComments(postId: string | null) {
  return useInfiniteQuery({
    queryKey: KEYS.list(postId!),
    queryFn: async ({ pageParam }) => {
      const { data } = await feedApi.getComments(postId!, pageParam);
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor ?? undefined : undefined,
    enabled: !!postId,
  });
}

export function useReplies(postId: string, commentId: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: KEYS.replies(postId, commentId),
    queryFn: async ({ pageParam }) => {
      const { data } = await feedApi.getReplies(postId, commentId, pageParam);
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor ?? undefined : undefined,
    enabled,
  });
}

export function useAddComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      postId,
      data,
    }: {
      postId: string;
      data: CreateCommentData;
    }) => {
      const { data: res } = await feedApi.addComment(postId, data);
      return res.comment;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: KEYS.list(variables.postId) });
      // Also invalidate feed to update comment count
      qc.invalidateQueries({ queryKey: ['feed', 'list'] });
    },
  });
}

export function useEditComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      postId,
      commentId,
      content,
    }: {
      postId: string;
      commentId: string;
      content: string;
    }) => {
      const { data: res } = await feedApi.editComment(postId, commentId, content);
      return res.comment;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: KEYS.list(variables.postId) });
    },
  });
}

export function useDeleteComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      postId,
      commentId,
    }: {
      postId: string;
      commentId: string;
    }) => {
      await feedApi.deleteComment(postId, commentId);
      return { postId, commentId };
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: KEYS.list(variables.postId) });
      qc.invalidateQueries({ queryKey: ['feed', 'list'] });
    },
  });
}
